import { useState, useEffect } from "react";
import { shallowEqual } from "react-redux";

// Hooks
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "app/hooks";

// Store
import { showLoader } from "store/slice/UI";
import { clearChart } from "store/slice/charts";
import { setSelectedSectors, setSelectedProducts, setSelectedCountry, setPeers } from "store/slice/Domain";
import { getInsightById, updateInsightChild } from "store/slice/Project/insights/insightsSlice";
import {
    addCompanySet,
    updateCompanySet,
    getCompanySetByPath,
} from "store/slice/Project/companiesSet/companiesSetSlice";

// Types
import { NavButtonProps } from "components/HeaderNavigation/NavButton";
import { Peer } from "services/company";
import { RequestInfo } from "store/slice/store.types";
import { ChipEntity, LocationFrom } from "types";
import { ProjectCompanySet } from "services/projects/project.types";

// Enums
import { urlPaths } from "enums/urlPaths";
import { saveProjectByPathService, updateProjectByPathService } from "services/projects/project.service";
import {
    getInsightPeersPath,
    getInsightRelatedSltPath,
    getInsightRelatedVcpData,
    getInsightFilters,
} from "pages/ProjectDashboard/adapters/formattedData";

// Utils
import { buildParamsString } from "utils";
import { isEmpty } from "lodash";

// Constants
import { PEERS_LIMIT } from "constants/constants";

type useDomainNavActionsProps = (
    peersData: Peer[],
    peersRequest: RequestInfo,
    selProducts: ChipEntity[],
    selSectors: ChipEntity[],
    selectedCountry: number | null,
    shouldContinue: boolean
) => {
    backButton: NavButtonProps;
    nextButton: NavButtonProps;
};

const useDomainNavActions: useDomainNavActionsProps = (
    peersData,
    peersRequest,
    selProducts,
    selSectors,
    selectedCountry,
    shouldContinue
) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const location = useLocation() as unknown as LocationFrom;

    // Url params
    const { companyId } = useParams();
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");
    const searchAiId = searchParams.get("searchAiId");

    const { data: insightData } = useAppSelector(getInsightById(Number(insightId)), shallowEqual);
    const hasRelatedFilters = Boolean(getInsightFilters(insightData)?.data.id);
    const relatedPeersPath = getInsightPeersPath(insightData)?.ref?.path;
    const relatedSltPath = getInsightRelatedSltPath(insightData)?.ref?.path;
    const relatedVcpData = getInsightRelatedVcpData(insightData)?.data;

    const relatedPeersData = useAppSelector(getCompanySetByPath(relatedPeersPath), shallowEqual);

    const [buttonLabel, setbuttonLabel] = useState("button.next");
    const [shouldDisableButton, setShouldDisableButton] = useState(false);

    const isFetchingPeers = peersRequest === "fetching";
    const canGotoInsights = Boolean(relatedPeersPath && relatedSltPath && relatedVcpData?.id);

    // if isEditing
    useEffect(() => {
        canGotoInsights && setbuttonLabel("button.finish.refinement");
    }, [canGotoInsights]);

    useEffect(() => {
        setShouldDisableButton(shouldContinue);
    }, [shouldContinue]);

    const handleBack = () => {
        const queryParams = buildParamsString({ projectId, insightId, searchAiId });
        const urlParams = `${companyId}?${queryParams}`;

        // Avoid back loop
        const avoidBackLoop =
            location.state?.from?.includes(urlPaths.AdvancedPeersSearch) ||
            location.state?.from?.includes(urlPaths.Team)
                ? undefined
                : location.state?.from;
        const backLocation =
            avoidBackLoop ||
            (canGotoInsights ? `${urlPaths.Projection}/${urlParams}` : `${urlPaths.ProjectDashboard}/${projectId}`);
        navigate(backLocation, { state: { from: `${urlPaths.Domain}/${urlParams}` } });
    };

    const handleGoToSetTeams = () => {
        const queryParams = buildParamsString({ projectId, insightId, searchAiId });
        const urlParams = `${companyId}?${queryParams}`;
        navigate(`${urlPaths.Team}/${urlParams}`);
    };

    const handleUpdateInsightPeers = async () => {
        if (projectId === null || insightId === null || !relatedPeersPath) return;

        const peersIds = peersData.map((p) => p.key).slice(0, PEERS_LIMIT);

        // Update peers company list
        try {
            await updateProjectByPathService({
                id: Number(projectId),
                project_path: relatedPeersPath,
                data: { company_ids: peersIds },
            });

            if (relatedPeersData) {
                const updatedData = {
                    ...relatedPeersData,
                    meta: { ...relatedPeersData.meta, count: peersIds.length },
                    data: { ...relatedPeersData.data, company_ids: peersIds },
                };
                dispatch(updateCompanySet(updatedData));
            }
        } catch (error) {
            console.error(error);
            dispatch(showLoader(false));
        }

        if (canGotoInsights) {
            const queryParams = buildParamsString({ projectId, insightId, searchAiId });
            const urlParams = `${companyId}?${queryParams}`;
            navigate(`${urlPaths.Projection}/${urlParams}`);
        } else {
            handleGoToSetTeams();
        }
    };

    const handleNext = async () => {
        dispatch(setPeers(peersData));
        dispatch(setSelectedProducts(selProducts));
        dispatch(setSelectedSectors(selSectors));
        dispatch(setSelectedCountry(selectedCountry));

        dispatch(showLoader(true));
        setShouldDisableButton(true);

        const payload = {
            data: {
                company: { id: Number(companyId) },
                sectors: selSectors.map((sector) => ({ id: sector.key, name: sector.value })),
                products: selProducts.map((product) => ({ id: product.key, name: product.value })),
                ...(selectedCountry
                    ? {
                          geo_boundary: {
                              country_id: selectedCountry,
                          },
                      }
                    : {}),
            },
            meta_ui: {
                products: selProducts.map((product) => ({
                    id: product.key,
                    selected: product.selected ? Boolean(product.selected) : false,
                    erasable: product.erasable ? Boolean(product.erasable) : false,
                })),
                sectors: selSectors.map((sector) => ({
                    id: sector.key,
                    selected: sector.selected ? Boolean(sector.selected) : false,
                    erasable: sector.erasable ? Boolean(sector.erasable) : false,
                })),
            },
        };

        if (hasRelatedFilters) {
            // Update Filters
            try {
                await updateProjectByPathService({
                    id: Number(projectId),
                    project_path: `/project_${projectId}/insight_list/insight_${insightId}/search_peers`,
                    data: payload,
                });

                const insightSearchPeers = getInsightFilters(insightData);

                if (insightId && insightSearchPeers) {
                    const updatedData = {
                        ...insightSearchPeers,
                        data: { ...insightSearchPeers.data, ...payload.data },
                        meta_ui: payload.meta_ui,
                    };

                    dispatch(
                        updateInsightChild({
                            data: updatedData,
                            id: Number(insightId),
                            type: "PROJECT_SEARCH_PEERS",
                        })
                    );
                }
            } catch (error) {
                console.error(error);
                dispatch(showLoader(false));
                setShouldDisableButton(false);
                return;
            }
        } else {
            // Save Filters
            try {
                const { data: insightSearchPeers } = await saveProjectByPathService({
                    id: Number(projectId),
                    project_path: `/project_${projectId}/insight_list/insight_${insightId}/search_peers`,
                    data: payload,
                    echo: true,
                });
                if (insightId && !isEmpty(insightSearchPeers)) {
                    dispatch(
                        updateInsightChild({
                            data: insightSearchPeers,
                            id: Number(insightId),
                            type: "PROJECT_SEARCH_PEERS",
                        })
                    );
                }
            } catch (error) {
                console.error(error);
                dispatch(showLoader(false));
                setShouldDisableButton(false);
                return;
            }
        }

        if (relatedPeersPath) {
            // Updating
            dispatch(clearChart());
            handleUpdateInsightPeers();
        } else {
            // Creating (creates peers list)
            let companySet = {} as ProjectCompanySet;
            try {
                const { data: companySets } = await saveProjectByPathService({
                    id: Number(projectId),
                    project_path: `/project_${projectId}/company_set_list`,
                    data: {
                        company_ids: peersData.map((p) => p.key).slice(0, PEERS_LIMIT),
                        name: `Domain Peers ` + `${new Date().getTime()}`.slice(-5),
                        type: "PEERS",
                    },
                    echo: true,
                });

                companySet = companySets.children.at(-1) as ProjectCompanySet;

                if (!isEmpty(companySet)) {
                    dispatch(addCompanySet([companySet]));
                }
            } catch (error) {
                console.error(error);
                dispatch(showLoader(false));
                setShouldDisableButton(false);
                return;
            }

            // Update ref on corresponding Insight
            try {
                const payload = {
                    data: {},
                    ref: {
                        path: `/project_${projectId}/company_set_list/company_set_${companySet.data.id}`,
                    },
                };

                await updateProjectByPathService({
                    id: Number(projectId),
                    project_path: `/project_${projectId}/insight_list/insight_${insightId}/peers`,
                    data: payload,
                });

                const insightCompanySetRef = getInsightPeersPath(insightData);

                if (insightCompanySetRef && insightId) {
                    const updatedData = {
                        ...insightCompanySetRef,
                        ref: payload.ref,
                    };

                    dispatch(
                        updateInsightChild({
                            data: updatedData,
                            id: Number(insightId),
                            type: "PROJECT_COMPANY_SET_REF",
                        })
                    );
                }
            } catch (error) {
                console.error(error);
                dispatch(showLoader(false));
                setShouldDisableButton(false);
                return;
            }

            handleGoToSetTeams();
        }
    };

    return {
        backButton: {
            onClick: handleBack,
        },
        nextButton: {
            translationId: buttonLabel,
            disabled: isFetchingPeers || shouldDisableButton,
            onClick: handleNext,
        },
    };
};

export default useDomainNavActions;
